import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BUTTON_SAVE, DefaultFormDialogComponent } from '../message/dialogs/default-form-dialog.component';
import { GaValidateService } from '../formly/ga-validate.service';
import { TranslationService } from '../translation/translation.service';
import { testEmailAddressFn } from '../formly/ga-formly.util';
import { EDITOR_TINYMCE } from '../editor/editor-config.service';
import { IContact } from '../../../type_definitions/datatypes';
import { util } from '../util';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import * as _ from 'underscore';
import { FormattingService } from '../services/formatting.service';
import { Override } from '../app-decorators';
import { CancelIdContact, ContactDataService } from '../contact/contact-data.service';

export interface ISendMailModel {
  to:(IContact | string)[];
  cc:(IContact | string)[];
  bcc:(IContact | string)[];
  subject:string;
  body:string;
  sendToInvoiceContact?: boolean;
}

@Component({
  selector: 'send-mail',
  templateUrl: '../message/dialogs/default-form-dialog.html',
  styleUrls: ['./send-mail.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SendMailComponent extends DefaultFormDialogComponent implements OnInit, OnChanges {
  public model: ISendMailModel = <any>{};
  public dialogClass: string = 'send-mail';

  @Input() public subject:string;
  @Input() public to:IContact[];
  @Input() public busy: boolean;
  @Input() public disabled: boolean;

  protected toField:IExtraFieldConfigurationObject;
  protected ccField:IExtraFieldConfigurationObject;
  protected bccField:IExtraFieldConfigurationObject;
  protected subjectField:IExtraFieldConfigurationObject;
  protected bodyField:IExtraFieldConfigurationObject;

  constructor(
    protected formlyService: GaFormlyService,
    protected contactDataService: ContactDataService,
    protected formattingService: FormattingService,
    validateService: GaValidateService,
    translationService: TranslationService
  ) {
    super(validateService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit(): void {
    this.buttons = [{
      id: BUTTON_SAVE,
      label: 'Send',
      className: 'btn btn-success',
      onClick: this.save,
      busy: this.busy,
      disabled: this.disabled
    }, {
      label: 'GA.BUTTONS.CANCEL',
      className: 'btn btn-danger',
      onClick: () => this.onCancel.emit()
    }];

    this.setData();
    this.fields = this.createFields();
  }

  @Override(DefaultFormDialogComponent)
  public ngOnChanges(changesObj: SimpleChanges) {
    if (changesObj.to) {
      this.to = _.clone(changesObj.to.currentValue);
      // this is for init the page
      this.model.to = this.to;

      // this is when form is ready
      this.form.patchValue({ to: this.to });
    }

    if (changesObj.busy) {
      this.busy = _.clone(changesObj.busy.currentValue);
      this.saveButtonBusy = this.busy;
    }

    if (changesObj.disabled) {
      this.disabled = _.clone(changesObj.disabled.currentValue);

      // fix fields undefined
      if (this.fields?.length) {
        this.setFormFieldDisabled(this.disabled);
      }
    }
  }

  public createFields(): IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];

    const labelClass:string = 'col-md-1';
    const valueClass:string = 'col-md-11';

    fields.push(
      this.toField = this.formlyService.aMultiSelect('to', 'To')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatContactWithCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.TO),
              cancel: () => {
                return this.contactDataService.cancelGetContacts(CancelIdContact.TO);
              }
            },
            customValueTestFn: testEmailAddressFn
          }
        })
        .build(),

      this.formlyService.aLink()
        .onClick(this.swapMailRecipients)
        .withExtraOptions({
          templateOptions: {
            icon: 'fa fa-random'
          }
        })
        .withoutWrapper()
        .withWrapperClass('swap-recipients')
        .build(),

      this.ccField = this.formlyService.aMultiSelect('cc', 'CC')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatContactWithCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter) => this.contactDataService.getContacts(filter, CancelIdContact.CC),
              cancel: () => {
                return this.contactDataService.cancelGetContacts(CancelIdContact.CC);
              }
            },
            customValueTestFn: testEmailAddressFn
          }
        })
        .build(),

      this.bccField = this.formlyService.aMultiSelect('bcc', 'BCC')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatContactWithCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter) => {
                return this.contactDataService.getContacts(filter, CancelIdContact.BCC);
              },
              cancel: () => {
                return this.contactDataService.cancelGetContacts(CancelIdContact.BCC);
              }
            },
            customValueTestFn: testEmailAddressFn
          }
        })
        .build(),

      this.subjectField = this.formlyService.anInput('subject', 'Subject')
        .isRequired()
        .withWrapperClass('subject-field')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.bodyField = this.formlyService.aTextarea('body', 'Body')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE
          }
        })
        .build()
    );

    return fields;
  }

  private setData():void {
    this.model.subject = this.subject;
  }

  public swapMailRecipients = ():void => {
    this.form.patchValue({ to: util.deepClone(this.model.cc), cc: util.deepClone(this.model.to) });
  };

  public cancel():void {
    this.onCancel.emit();
  }

  public setFormFieldDisabled(disabled:boolean) {
    this.disabled = disabled;
    this.buttons[0].disabled = disabled;
    this.toField.templateOptions.disabled = disabled;
    this.ccField.templateOptions.disabled = disabled;
    this.bccField.templateOptions.disabled = disabled;
    this.subjectField.templateOptions.disabled = disabled;
    this.bodyField.templateOptions.disabled = disabled;
  }
}